import { useEffect, useRef } from "react";
import { useSpring, useMotionValueEvent } from "framer-motion";

export function AnalogueMeter({ value, min = 0, max = 100, bands = [], label, unit, sublabel, testId }) {
  const cx = 100, cy = 100, r = 78;
  const needleGroupRef = useRef(null);
  const startAngle = -120, endAngle = 120;
  const pct = Math.max(0, Math.min(1, (value - min) / (max - min || 1)));
  const needleAngle = startAngle + pct * (endAngle - startAngle);
  const springAngle = useSpring(needleAngle, { stiffness: 55, damping: 11 });

  useMotionValueEvent(springAngle, "change", (latest) => {
    needleGroupRef.current?.setAttribute("transform", `rotate(${latest} ${cx} ${cy})`);
  });
  useEffect(() => { springAngle.set(needleAngle); }, [needleAngle, springAngle]);
  useEffect(() => {
    needleGroupRef.current?.setAttribute("transform", `rotate(${springAngle.get()} ${cx} ${cy})`);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const arcPoint = (angleDeg, radius) => {
    const rad = ((angleDeg - 90) * Math.PI) / 180;
    return [cx + radius * Math.cos(rad), cy + radius * Math.sin(rad)];
  };
  const describeArc = (a1, a2, radius) => {
    const [x1, y1] = arcPoint(a1, radius);
    const [x2, y2] = arcPoint(a2, radius);
    const largeArc = a2 - a1 > 180 ? 1 : 0;
    return `M ${x1} ${y1} A ${radius} ${radius} 0 ${largeArc} 1 ${x2} ${y2}`;
  };

  return (
    <div className="flex flex-col items-center" data-testid={testId}>
      <svg viewBox="0 0 200 200" width={160} height={160}>
        <path d={describeArc(startAngle, endAngle, r)} stroke="#27272A" strokeWidth="10" fill="none" strokeLinecap="round" />
        {bands.map((b, i) => {
          const a1 = startAngle + ((b.from - min) / (max - min)) * (endAngle - startAngle);
          const a2 = startAngle + ((b.to - min) / (max - min)) * (endAngle - startAngle);
          return <path key={i} d={describeArc(a1, a2, r)} stroke={b.color} strokeWidth="10" fill="none" />;
        })}
        {[0, 0.25, 0.5, 0.75, 1].map((f) => {
          const a = startAngle + f * (endAngle - startAngle);
          const [x1, y1] = arcPoint(a, r - 10);
          const [x2, y2] = arcPoint(a, r + 2);
          return <line key={f} x1={x1} y1={y1} x2={x2} y2={y2} stroke="var(--oim-text-dim)" strokeWidth="1.5" />;
        })}
        <g ref={needleGroupRef}>
          <polygon points={`${cx-4},${cy} ${cx+4},${cy} ${cx},${cy-r+8}`} fill="#FF3B30" />
          <polygon points={`${cx-3},${cy} ${cx+3},${cy} ${cx},${cy+20}`} fill="#7A1F1B" />
        </g>
        <circle cx={cx} cy={cy} r="7" fill="#3f3f46" stroke="#71717A" />
      </svg>
      <div className="text-center -mt-4">
        <div className="font-mono-data text-2xl font-bold text-[var(--oim-text)]">
          {value?.toFixed ? value.toFixed(1) : value} <span className="text-xs text-[var(--oim-text-muted)]">{unit}</span>
        </div>
        <div className="text-[10px] uppercase tracking-widest text-[var(--oim-text-muted)] font-heading">{label}</div>
        {sublabel && <div className="text-[10px] text-[var(--oim-text-dim)]">{sublabel}</div>}
      </div>
    </div>
  );
}
